/**
 * The pre-land judge (R-MS-5, ADR 0086 phase 2, ORGU-157). Before Orgus lands a mission's branch,
 * a model judges each acceptance criterion of the item over the change and the checks that ran.
 * `prelandVerdict` turns those judgements and the item's facts into one of three outcomes:
 *
 *   land     every criterion met (or met and not checkable once), nothing for a person
 *   back     a criterion is not met: the branch goes back to its agent with the gap; it does not land
 *   person   a person decides, with the reason and Orgus's recommendation, when the item failed
 *            twice, a criterion stays uncheckable, a risky path is touched, the item falls in the
 *            sample, or the project chose Review People
 *
 * The sample is recomputable by anyone from the item key and the head sha (`prelandSampled`).
 * The review after landing (review.ts) stays the fallback for work that landed without a verdict.
 */
import type { CriterionJudgement, ItemReviewMode } from './review.ts';

/** One in this many items (5%) is shown to a person even when every criterion is met. */
export const PRELAND_SAMPLE_PERCENT = 5;
/** Failed judgements of the same item before a person is asked. */
export const PRELAND_FAILURES_MAX = 2;

/** Paths where a change waits for a person: secrets, auth, signing, release, CI, policy, migrations. */
export const RISKY_PATHS: readonly RegExp[] = Object.freeze([
  /(^|\/)\.env(\.|$)/i,
  /(^|\/)\.github\/workflows\//i,
  /(^|\/)(auth|signin|sign-in|secrets?|signing|release)(\/|\.|-)/i,
  /(^|\/)migrations?\//i,
  /(^|\/)packages\/policy\//i,
  /(^|\/)(pnpm-lock\.yaml|package-lock\.json|yarn\.lock)$/i,
]);

/** The risky paths a change touches, in order. */
export function riskyPaths(files: readonly string[], patterns: readonly RegExp[] = RISKY_PATHS): string[] {
  return files.map((f) => f.replace(/\\/g, '/')).filter((f) => patterns.some((p) => p.test(f)));
}

/** FNV-1a, 32 bits: small, stable on every runtime, enough to spread a sample. */
function fnv1a(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h >>> 0;
}

/** True when this item at this head falls in the person sample. Recomputable: fnv1a(`${key}:${sha}`) % 100 < 5. */
export function prelandSampled(itemKey: string, headSha: string, percent = PRELAND_SAMPLE_PERCENT): boolean {
  return fnv1a(`${itemKey.trim().toUpperCase()}:${headSha.trim().toLowerCase()}`) % 100 < percent;
}

export interface PrelandFacts {
  itemKey: string;
  headSha: string;
  files: readonly string[];
  /** Earlier pre-land judgements of this item that found a criterion not met. */
  failures: number;
  mode: ItemReviewMode;
}

export type PrelandVerdict =
  | { do: 'land'; criteria: CriterionJudgement[] }
  | { do: 'back'; criteria: CriterionJudgement[]; gap: string }
  | { do: 'person'; criteria: CriterionJudgement[]; reason: string; recommendation: 'land' | 'send back' };

/** What happens to the branch, from the judgements and the facts. Pure. */
export function prelandVerdict(criteria: readonly CriterionJudgement[], f: PrelandFacts): PrelandVerdict {
  const all = [...criteria];
  const unmet = all.filter((c) => c.verdict === 'not_met');
  const gap = unmet.map((c) => `${c.id}: ${c.evidence || 'not met'}`).join('; ');
  const recommendation = unmet.length ? 'send back' : 'land';
  if (unmet.length && f.failures + 1 >= PRELAND_FAILURES_MAX) return { do: 'person', criteria: all, reason: `Failed the judge ${f.failures + 1} times: ${gap}`, recommendation };
  if (unmet.length) return { do: 'back', criteria: all, gap };
  const risky = riskyPaths(f.files);
  if (risky.length) return { do: 'person', criteria: all, reason: `Touches a risky path: ${risky.slice(0, 5).join(', ')}`, recommendation };
  if (!all.length) return { do: 'person', criteria: all, reason: 'The item has no criterion the judge could check', recommendation };
  const uncheckable = all.filter((c) => c.verdict === 'not_checkable');
  if (uncheckable.length) return { do: 'person', criteria: all, reason: `Not checkable: ${uncheckable.map((c) => c.id).join(', ')}`, recommendation };
  if (f.mode === 'people') return { do: 'person', criteria: all, reason: 'The project chose Review People', recommendation };
  if (prelandSampled(f.itemKey, f.headSha)) return { do: 'person', criteria: all, reason: `In the ${PRELAND_SAMPLE_PERCENT}% sample`, recommendation };
  return { do: 'land', criteria: all };
}
